import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';

import BoonLogo from '../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../assets/images/BoonViewIcon.svg';
import { ReactComponent as LinkedinIcon } from '../../assets/images/linkedin-icon.svg';
import { ReactComponent as WebIcon } from '../../assets/images/web-icon.svg';
import { ApplicationFlowStepper } from '../../components/ApplicationFlowStepper/ApplicationFlowStepper';
import { FileUpload } from '../../components/FileUpload/FileUpload';
import { FormikMultiSelectInput } from '../../components/FormikMultiSelectInput/FormikMultiSelectInput';
import { FormikSelectInput } from '../../components/FormikSelectInput/FormikSelectInput';
import { FormikTextArea } from '../../components/FormikTextArea/FormikTextArea';
import { FormikTextInput } from '../../components/FormikTextInput/FormikTextInput';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import NotFound from '../../components/NotFound/NotFound';
import Notification from '../../components/Notification/Notification';
import { OpeningSelect } from '../../components/OpeningSelect/OpeningSelect';
import { ReferralProviderSelect } from '../../components/ReferralProviderSelect/ReferralProviderSelect';
import ButtonWithIcon from '../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { FormikUrlInput } from '../../components/shared/FormikUrlInput/FormikUrlInput';
import Header from '../../components/shared/Header/Header';
import { YourInfoSection } from '../../components/shared/YourInfoSection/YourInfoSection';
import { IsMobile } from '../../helpers/IsMobile';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useOrganizationSelector } from '../../hooks/useOrganizationSelector';
import useReferralProviderData, { persistReferralProviderData } from '../../hooks/useReferralProviderData';
import { getCompanyProfile } from '../../redux/actions/companyProfileActions';
import { setCurrentOrganization } from '../../redux/actions/organizationActions';
import {
  useApplyOnReferredJobMutation,
  useCandidateAlreadyExistsMutation,
  useCreateCandidateMutation,
  useSubmitApplicationMutation,
} from '../../redux/apis/applicationApi';
import { useGetUserResumesQuery, useSearchJobsQuery, useUpdatePdfFileMutation } from '../../redux/apis/jobsApi';
import {
  useGetOrganizationReferralFormPermissionsQuery,
  useGetOrganizationStrengthenQuestionsQuery,
} from '../../redux/apis/organizationApi';
import { useGetReferDataQuery } from '../../redux/apis/referralApi';
import { useGetCurrentUserQuery, useGetUserProfileQuery } from '../../redux/apis/userApi';
import urls from '../../routing/urls';
import { selectCompanyProfile } from '../../selectors/companyProfileSelectors';
import { selectCurrentOrganization } from '../../selectors/organizationSelectors';

import styles from './ApplicationFlow.module.scss';

const buildValidationSchema = (applicationFormPermissions, t) => {
  const customQuestionsSchema = {};
  const pleaseAnswerQuestion = t('validation:generic.pleaseAnswerQuestion');

  if (
    applicationFormPermissions?.add_application_form_custom_questions &&
    applicationFormPermissions?.application_form_custom_questions
  ) {
    applicationFormPermissions.application_form_custom_questions.forEach((question) => {
      const fieldName = `application_question_${question.id}`;

      if (question.required) {
        if (question.fieldType === 'single_select_dropdown') {
          customQuestionsSchema[fieldName] = Yup.mixed()
            .required(pleaseAnswerQuestion)
            .test('not-empty', pleaseAnswerQuestion, (value) => value !== null && value !== undefined && value !== '');
        } else if (question.fieldType === 'multi_select_dropdown') {
          customQuestionsSchema[fieldName] = Yup.array()
            .required(pleaseAnswerQuestion)
            .test('not-empty', pleaseAnswerQuestion, (value) => value && Array.isArray(value) && value.length > 0);
        } else {
          customQuestionsSchema[fieldName] = Yup.string()
            .required(pleaseAnswerQuestion)
            .test('not-empty', pleaseAnswerQuestion, (value) => value && value.trim() !== '');
        }
      } else if (question.fieldType === 'single_select_dropdown') {
        customQuestionsSchema[fieldName] = Yup.mixed().optional();
      } else if (question.fieldType === 'multi_select_dropdown') {
        customQuestionsSchema[fieldName] = Yup.array().optional();
      } else {
        customQuestionsSchema[fieldName] = Yup.string().optional();
      }
    });
  }

  return Yup.object().shape({
    opening: Yup.mixed().test('opening-required', t('validation:select.opening'), (value) => {
      if (!value || value === '') return false;
      return (
        typeof value === 'number' || (typeof value === 'string' && !Number.isNaN(Number(value)) && Number(value) > 0)
      );
    }),
    messageToEmployer: Yup.string().required(t('validation:message.toEmployerRequired')),
    firstName: Yup.string().required(t('validation:name.firstNameRequiredShort')),
    lastName: Yup.string().required(t('validation:name.lastNameRequiredShort')),
    email: Yup.string().email(t('validation:email.invalid')).required(t('validation:email.required')),
    ...customQuestionsSchema,
  });
};

export const ApplicationFlow = () => {
  const { t } = useTranslation('referral');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const opening = searchParams.get('apply_for');
  const referralToken = searchParams.get('refer_token');
  const referralProviderId = searchParams.get('rp');
  const { referralProviderData } = useReferralProviderData();
  const { data: referralProviderProfile } = useGetUserProfileQuery(referralProviderId, {
    skip: !referralProviderId,
  });
  const [jobId, setJobId] = useState(null);
  const { isAuthenticated } = useCurrentUser();
  const { data: currentUserData } = useGetCurrentUserQuery(undefined, { skip: !isAuthenticated });
  const { data: referData } = useGetReferDataQuery(referralToken, { skip: !referralToken });
  const currentUser = currentUserData?.payload;

  const [submitApplication, { isLoading: isSubmitting }] = useSubmitApplicationMutation();
  const [createCandidate, { isLoading: isCreatingCandidate }] = useCreateCandidateMutation();
  const [applyOnReferredJob, { isLoading: isApplyingOnReferredJob }] = useApplyOnReferredJobMutation();
  const [candidateAlreadyExists] = useCandidateAlreadyExistsMutation();
  const [email, setEmail] = useState('');
  const [isCandidateAlreadyExists, setIsCandidateAlreadyExists] = useState(false);
  const isMobile = IsMobile();
  const setFieldErrorRef = useRef(null);
  useOrganizationSelector();

  // Prefer live ?rp= profile (job share / my referral link) over stale localStorage.
  const resolvedReferralProviderData = referralProviderProfile?.payload || referralProviderData || null;

  useEffect(() => {
    if (referralProviderId && referralProviderProfile?.payload) {
      persistReferralProviderData(referralProviderProfile.payload);
    }
  }, [referralProviderId, referralProviderProfile]);

  const referralProviderOptions = useMemo(() => {
    if (resolvedReferralProviderData) {
      const { user } = resolvedReferralProviderData;

      return [
        {
          label: user?.full_name,
          value: user?.id,
          provider: user,
        },
      ];
    }

    if (referData?.payload?.all_referrers?.length) {
      return referData?.payload?.all_referrers?.map((referrer) => ({
        label: referrer?.full_name,
        value: referrer?.id,
        provider: referrer,
      }));
    }

    return [];
  }, [resolvedReferralProviderData, referData]);

  const personalInfo = useMemo(
    () =>
      currentUser
        ? {
            firstName: currentUser.user?.first_name,
            lastName: currentUser.user?.last_name,
            email: currentUser.user?.email,
            avatar_thumb: currentUser.user?.avatar_thumb,
          }
        : undefined,
    [currentUser]
  );

  const { companyName } = useParams();

  const companyProfile = useSelector(selectCompanyProfile);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const company = companyProfile || currentOrganization;
  const { data: jobs } = useSearchJobsQuery(
    {
      searchTerm: '',
      organizationId: company?.name === 'Boon' ? 'Boon' : company?.id,
      rp: referralProviderId || resolvedReferralProviderData?.user?.slug,
    },
    { skip: !company?.id && company?.name !== 'Boon' }
  );

  const selectedJob = useMemo(() => {
    if (opening) {
      return jobs?.find((job) => job.uuid === opening);
    }
    if (jobId) {
      return jobs?.find((job) => job.id === jobId);
    }
  }, [jobs, opening, jobId]);
  const { data: userResumesData } = useGetUserResumesQuery(undefined, { skip: !isAuthenticated || !currentUser });
  const resumes = userResumesData?.payload?.user_resume;
  const [updatePdfFile] = useUpdatePdfFileMutation();

  // Get organization permissions for application form custom questions
  const { data: organizationPermissions } = useGetOrganizationReferralFormPermissionsQuery(
    { organizationId: company?.id },
    { skip: !company?.id }
  );
  const applicationFormPermissions = organizationPermissions?.payload || {
    add_application_form_custom_questions: false,
    application_form_custom_questions: [],
  };

  const orgId = company?.name === 'Boon' ? company?.id : company?.id;
  const { data: strengthenQuestionsData } = useGetOrganizationStrengthenQuestionsQuery(
    { organizationId: orgId },
    { skip: !orgId }
  );
  const applicationStrengthenQuestions = strengthenQuestionsData?.payload || {
    enable_application_questions: true,
    current_title: true,
    current_employer: true,
    current_location: true,
    diversity_hire: true,
    joining_date: true,
    why_are_they_good_fit: true,
  };
  const hasAnyApplicationQuestionEnabled =
    applicationStrengthenQuestions.enable_application_questions &&
    (applicationStrengthenQuestions.current_title ||
      applicationStrengthenQuestions.current_employer ||
      applicationStrengthenQuestions.current_location ||
      applicationStrengthenQuestions.why_are_they_good_fit ||
      applicationStrengthenQuestions.joining_date ||
      applicationStrengthenQuestions.diversity_hire);
  const showStrengthenStep = !!hasAnyApplicationQuestionEnabled;

  const initialLicenseAvailable = useMemo(() => {
    if (!resumes) return [];

    return resumes.map((file) => ({
      id: file.id,
      name: file.pdf,
      size: 0,
      type: file.pdf.endsWith('.pdf')
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      is_selected: file.is_selected,
    }));
  }, [resumes]);
  const buildApplicationQuestionsInitialValues = () => {
    const values = {};
    if (
      applicationFormPermissions?.add_application_form_custom_questions &&
      applicationFormPermissions?.application_form_custom_questions
    ) {
      applicationFormPermissions.application_form_custom_questions.forEach((question) => {
        const fieldName = `application_question_${question.id}`;
        if (question.fieldType === 'multi_select_dropdown') {
          values[fieldName] = [];
        } else {
          values[fieldName] = '';
        }
      });
    }
    return values;
  };

  const initialValues = useMemo(
    () => ({
      opening: opening ? jobs?.find((job) => job.uuid === opening)?.id || '' : referData?.payload?.job?.id || '',
      messageToEmployer: t('apply.defaultMessage'),
      firstName: personalInfo?.firstName || referData?.payload?.subject_user?.first_name || '',
      lastName: personalInfo?.lastName || referData?.payload?.subject_user?.last_name || '',
      email: personalInfo?.email || referData?.payload?.subject_user?.email || '',
      license_available: initialLicenseAvailable,
      number_of_links: [
        {
          label: t('common:labels.linkedin'),
          type: 'ln',
          value: referData?.payload?.link_urls?.[0]?.value || 'www.linkedin.com/in/',
        },
        ...(referData?.payload?.link_urls?.slice(1) || []).map((link) => ({
          label: t('common:labels.website'),
          type: 'pw',
          value: link?.value || '',
        })),
      ],
      main_referrer_id:
        referralProviderOptions.length > 0 && referralProviderOptions[0].provider.email !== currentUser?.user?.email
          ? referralProviderOptions[0].value
          : '',
      ...buildApplicationQuestionsInitialValues(),
    }),
    [
      jobs,
      company,
      personalInfo,
      initialLicenseAvailable,
      referralProviderOptions,
      currentUser,
      referData,
      applicationFormPermissions,
    ]
  );

  const validationSchema = useMemo(
    () => buildValidationSchema(applicationFormPermissions, t),
    [applicationFormPermissions, t]
  );

  const handleSubmit = async (values, setFieldValue) => {
    const candidateAlreadyExistsResponse = await candidateAlreadyExists({
      organizationId: company?.id,
      values: { from_email: values.email, job_id: values.opening, subject_email: values.email },
    }).unwrap();

    if (candidateAlreadyExistsResponse.message !== 'OK') {
      Notification.error(candidateAlreadyExistsResponse.error);
      return;
    }

    const formData = new FormData();

    (values.license_available || []).forEach((file) => {
      if (file && !file.id && file.is_selected) {
        formData.append('files[]', file);
      }
    });

    // Build application form custom questions
    const applicationFormCustomQuestions = [];
    if (
      applicationFormPermissions?.add_application_form_custom_questions &&
      applicationFormPermissions?.application_form_custom_questions
    ) {
      applicationFormPermissions.application_form_custom_questions.forEach((question) => {
        const fieldName = `application_question_${question.id}`;
        const answer = values[fieldName];

        if (answer !== undefined && answer !== null && answer !== '') {
          applicationFormCustomQuestions.push({
            question: question.title,
            answer,
            fieldType: question.fieldType,
            custom_field_key: question.custom_field_key,
            form_type: 'application_form',
          });
        }
      });
    }

    const userData = {
      links_json: JSON.stringify(
        values.number_of_links.filter((link) => link.value && link.value !== 'www.linkedin.com/in/')
      ),
      message: values.messageToEmployer,
      name: `${values.firstName} ${values.lastName}`.trim(),
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      user_id: currentUser?.user?.id || null,
      pdf_file_ids:
        values.license_available?.filter((file) => file.id && file.is_selected).map((file) => file.id) || [],
      job_id: values.opening,
      current_title: '',
      current_employer: '',
      current_skills: [],
      new_skills: [],
      license_available: '',
      joining_date: '',
      main_referrer_id: values.main_referrer_id || null,
      public_job_application: !!referralToken,
      refer_request_id: referData?.payload?.refer_request?.id || null,
      origin_channel: resolvedReferralProviderData ? 'my_referral_link' : 'user_web_app',
      application_form_custom_questions: applicationFormCustomQuestions,
    };

    let submitFunction;
    if (referralToken) {
      submitFunction = applyOnReferredJob;
      formData.append('user_data', JSON.stringify(userData));
    } else if (companyName && !referralToken) {
      submitFunction = submitApplication;
      formData.append('user_data', JSON.stringify(userData));
    } else {
      submitFunction = createCandidate;
      formData.append('refer_data', JSON.stringify(userData));
    }

    submitFunction({
      organizationId: company?.id,
      applicationData: formData,
    })
      .unwrap()
      .then((response) => {
        if (response.error) {
          Notification.error(typeof response.error === 'string' ? response.error : response.error.message);
          if (response.error.includes('Malicious file detected')) {
            setFieldValue('license_available', []);
          }
          return;
        }

        const applicationId = response.payload.candidate_uuid;
        if (showStrengthenStep) {
          const url = companyName ? urls.companyApplicationFlowStrengthen : urls.applicationFlowStrengthen;
          navigate(url.replace(':companyName', companyName || '').replace(':applicationId', applicationId), {
            state: { ...values, response: response.payload },
          });
        } else {
          const url = companyName ? urls.companyApplicationFlowSuccess : urls.applicationFlowSuccess;
          navigate(url.replace(':companyName', companyName || '').replace(':applicationId', applicationId), {
            state: { ...values, response: response.payload },
          });
        }
      });
  };

  useEffect(() => {
    const checkCandidateAlreadyExists = async () => {
      if (selectedJob?.id && (email || personalInfo?.email)) {
        const candidateAlreadyExistsResponse = await candidateAlreadyExists({
          organizationId: company?.id,
          values: {
            from_email: email || personalInfo?.email,
            job_id: selectedJob?.id,
            subject_email: email || personalInfo?.email,
          },
        }).unwrap();

        setIsCandidateAlreadyExists(candidateAlreadyExistsResponse.message !== 'OK');
      }
    };
    checkCandidateAlreadyExists();
  }, [candidateAlreadyExists, company, email, personalInfo?.email, selectedJob?.id]);

  useEffect(() => {
    if (isCandidateAlreadyExists && !currentUser && setFieldErrorRef.current) {
      setFieldErrorRef.current('email', t('apply.cannotApplyTwice'));
    }
  }, [isCandidateAlreadyExists, currentUser]);

  useEffect(() => {
    if (companyName) {
      dispatch(getCompanyProfile(companyName, navigate));
    }
  }, [dispatch, companyName, navigate]);

  useEffect(() => {
    if (company) {
      dispatch(setCurrentOrganization(company));
    }
  }, [company]);

  if (referData?.error === 'Invalid token') {
    return <NotFound />;
  }

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className={styles.outerContainer}>
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('apply.title')}
          backButton={isAuthenticated && !companyName ? t('apply.back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.container}>
          <ApplicationFlowStepper currentStep={1} showStrengthenStep={showStrengthenStep} />

          <div className={styles.formSection}>
            <div className={styles.formHeader}>
              <h2>{t('apply.applyTo', { company: company?.name })}</h2>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, { setFieldValue }) => {
                handleSubmit(values, setFieldValue);
              }}
              enableReinitialize
            >
              {({ values, setFieldValue, setFieldTouched, setFieldError, errors, touched }) => {
                setFieldErrorRef.current = setFieldError;

                return (
                  <Form>
                    <div className={styles.form}>
                      {referralProviderOptions.length > 0 &&
                        !(
                          referralProviderOptions.length === 1 &&
                          referralProviderOptions[0].provider.email === currentUser?.user?.email
                        ) && (
                          <div className="space-y-[6px]">
                            <label htmlFor="main_referrer_id" className={styles.formLabel}>
                              {t('apply.referralProvider')}
                            </label>
                            <ReferralProviderSelect
                              options={referralProviderOptions}
                              value={values.main_referrer_id}
                              onChange={(selectedOption) => {
                                setFieldValue('main_referrer_id', selectedOption ? selectedOption.value : '');
                              }}
                              isDisabled={referralProviderOptions.length === 1}
                            />
                          </div>
                        )}
                      <div>
                        <OpeningSelect
                          organizationId={company?.name === 'Boon' ? 'Boon' : company?.id}
                          value={values.opening}
                          applicationFlow
                          isReferredJob={!!referData?.payload?.job}
                          onChange={(selectedOption) => {
                            setFieldValue('opening', selectedOption ? selectedOption.value : '');
                            setJobId(selectedOption ? selectedOption.value : null);
                            searchParams.delete('apply_for');
                            setSearchParams(searchParams);
                          }}
                          disabled={referralToken}
                          placeholder={t('apply.selectOpening')}
                          hasError={(currentUser && isCandidateAlreadyExists) || (touched.opening && errors.opening)}
                        />
                        {currentUser && isCandidateAlreadyExists && (
                          <p className={styles.errorMessage}>*{t('apply.alreadyApplied')}</p>
                        )}
                        {touched.opening && errors.opening && !(currentUser && isCandidateAlreadyExists) && (
                          <p className={styles.errorMessage}>{errors.opening}</p>
                        )}
                      </div>
                      <FormikTextArea
                        name="messageToEmployer"
                        label={t('apply.messageToEmployer')}
                        placeholder={t('apply.messagePlaceholder')}
                        maxLength={300}
                        showCharacterCount
                        rows={4}
                        required
                      />
                      {!currentUser ? (
                        <>
                          <div className={styles.row}>
                            <FormikTextInput
                              name="firstName"
                              label={t('candidateInfo.firstName')}
                              placeholder={t('candidateInfo.firstNamePlaceholder')}
                              isRequired
                            />
                            <div className="mt-[27px]">
                              <FormikTextInput
                                name="lastName"
                                label={t('candidateInfo.lastName')}
                                placeholder={t('candidateInfo.lastNamePlaceholder')}
                                shouldDisplayLabel={false}
                              />
                            </div>
                          </div>

                          <FormikTextInput
                            name="email"
                            type="email"
                            label={t('candidateInfo.email')}
                            placeholder={t('candidateInfo.emailPlaceholder')}
                            disabled={referData?.payload?.subject_user?.email}
                            onBlur={() => {
                              setFieldTouched('email', true);
                              setEmail(values.email);
                            }}
                            isRequired
                          />
                        </>
                      ) : (
                        <YourInfoSection personalInfo={personalInfo} />
                      )}

                      <FileUpload
                        fieldName="license_available"
                        files={values.license_available}
                        setFieldValue={setFieldValue}
                        onRemove={(id) => updatePdfFile({ id })}
                      />

                      {/* Application Form Custom Questions */}
                      {applicationFormPermissions?.add_application_form_custom_questions &&
                        applicationFormPermissions?.application_form_custom_questions?.length > 0 && (
                          <div className={styles.customQuestionsContainer}>
                            {[...applicationFormPermissions.application_form_custom_questions]
                              .sort((a, b) => a.order - b.order)
                              .map((question) => {
                                const fieldName = `application_question_${question.id}`;

                                if (question.fieldType === 'text') {
                                  return (
                                    <FormikTextInput
                                      key={question.id}
                                      name={fieldName}
                                      label={question.title}
                                      placeholder={t('common:placeholders.writeYourAnswer')}
                                      isRequired={question.required}
                                    />
                                  );
                                }

                                if (question.fieldType === 'single_select_dropdown') {
                                  const options = question.options.map((option) => ({
                                    value: option,
                                    label: option,
                                  }));

                                  return (
                                    <FormikSelectInput
                                      key={question.id}
                                      name={fieldName}
                                      label={question.title}
                                      placeholder={t('common:placeholders.selectOption')}
                                      options={options}
                                      isRequired={question.required}
                                    />
                                  );
                                }

                                if (question.fieldType === 'multi_select_dropdown') {
                                  const options = question.options.map((option) => ({
                                    value: option,
                                    label: option,
                                  }));

                                  return (
                                    <FormikMultiSelectInput
                                      key={question.id}
                                      name={fieldName}
                                      label={question.title}
                                      placeholder={t('common:placeholders.selectOptions')}
                                      options={options}
                                      isRequired={question.required}
                                    />
                                  );
                                }

                                return null;
                              })}
                          </div>
                        )}

                      <div className={styles.urlInputContainer}>
                        <label className={styles.additionalUrlsLabel} htmlFor="number_of_links">
                          {t('apply.links')}
                        </label>
                        {/* LinkedIn URL as a special link */}
                        <div className={styles.urlInputRow}>
                          <span className={styles.urlInputIcon}>
                            <LinkedinIcon className="w-[20px] h-[20px]" />
                          </span>
                          <FormikUrlInput
                            name="number_of_links.0.value"
                            label={t('common:labels.linkedinUrl')}
                            placeholder=""
                            shouldDisplayLabel={false}
                            isLinkedIn
                          />
                        </div>
                        <FieldArray name="number_of_links">
                          {({ push, remove }) => (
                            <div>
                              {values.number_of_links.slice(1).map((link, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <div key={`additional-link-${index}`} className={styles.urlInputRow}>
                                  <span className={styles.urlInputIcon}>
                                    <WebIcon />
                                  </span>
                                  <FormikUrlInput
                                    name={`number_of_links.${index + 1}.value`}
                                    label={t('common:labels.additionalUrl')}
                                    placeholder=""
                                    shouldDisplayLabel={false}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => remove(index + 1)}
                                    className={styles.removeUrlButton}
                                  >
                                    &#x2715;
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => push({ label: t('common:labels.website'), type: 'pw', value: '' })}
                                className={styles.addMoreButton}
                              >
                                {t('apply.addMore')}
                              </button>
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                    <div className={styles.nextButtonContainer}>
                      <ButtonWithIcon
                        type="submit"
                        variant="secondary-36"
                        text={
                          <span className="flex items-center gap-[8px]">
                            {(isSubmitting || isCreatingCandidate || isApplyingOnReferredJob) && (
                              <Spinner animation="border" size="sm" />
                            )}{' '}
                            {t('apply.submit')}
                          </span>
                        }
                        textClassName="text-white"
                        containerClass="w-[142px] self-end"
                        disabled={
                          isSubmitting || isCreatingCandidate || isCandidateAlreadyExists || isApplyingOnReferredJob
                        }
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};
